/*
  Brilinton Product Context — R44-268.

  One customer-context contract for product-page consumers. It distinguishes an
  explicit customer/device context from a theme default, updates BandWare PDP
  clarity, and projects the same Apple Watch context into BandWare cards inside
  Related Products.
*/
(() => {
  'use strict';

  const GLOBAL_KEY = '__brilintonProductContextR44268';
  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].refresh?.(document);
    return;
  }

  const DATA_SELECTOR = '[data-br-product-context-data]';
  const BANDWARE_CARD_SELECTOR = '[data-bandware-recommendations-scope] [data-bandware-card="true"]';
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const normalizeWear = (value) => {
    const key = normalize(value).replace(/[^a-z0-9]+/g, '-');
    if (key.includes('royal')) return 'royal-pop';
    if (key.includes('apple')) return 'apple-watch';
    return '';
  };
  const normalizeSetMode = (value, forceComplete = false) => {
    if (forceComplete) return 'complete';
    return /^(?:only|bandware-only|bandware)$/i.test(String(value || '')) ? 'bandware-only' : 'complete';
  };

  const wearFromCollectionReferrer = () => {
    if (!document.referrer) return '';
    try {
      const referrer = new URL(document.referrer);
      if (referrer.origin !== window.location.origin) return '';
      const path = referrer.pathname.replace(/\/+$/, '');
      if (path.endsWith('/collections/apple-watch-bands')) return 'apple-watch';
      if (path.endsWith('/collections/royal-pop-bands')) return 'royal-pop';
    } catch (_) {}
    return '';
  };
  const clone = (value) => JSON.parse(JSON.stringify(value || {}));
  const asBoolean = (value, fallback = false) => typeof value === 'boolean' ? value : fallback;

  const parseJson = (node, fallback = null) => {
    if (!node) return fallback;
    try { return JSON.parse(node.textContent || ''); } catch (_) { return fallback; }
  };

  const queryWithin = (scope, selector) => {
    const root = scope?.querySelectorAll ? scope : document;
    const nodes = [];
    if (root.nodeType === 1 && root.matches?.(selector)) nodes.push(root);
    root.querySelectorAll?.(selector).forEach((node) => nodes.push(node));
    return nodes;
  };

  const compactImageUrl = (source, width) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.origin === window.location.origin
        ? `${url.pathname}${url.search}${url.hash}`
        : url.toString();
    } catch (_) {
      return source;
    }
  };

  class ProductContextService {
    constructor() {
      this.data = {};
      this.state = {};
      this.initialized = false;
      this.revision = 0;
      this.cardFrame = 0;
      this.bound = false;
      this.bind();
    }

    bind() {
      if (this.bound) return;
      this.bound = true;

      document.addEventListener('bandware:context-change', (event) => {
        const detail = event.detail || {};
        const source = detail.source || 'bandware-selector';
        const incomingConfirmed = asBoolean(detail.customerConfirmed, asBoolean(detail.confirmed, false));
        const effectiveSource = source === 'initial' && this.state.customerConfirmed
          ? (this.state.source || source)
          : source;

        // A BandWare element still renders an Apple Watch default before the customer
        // acts. Do not let that unconfirmed initial default erase a context already
        // resolved from the originating Apple Watch / Royal Pop collection.
        if (!incomingConfirmed && source === 'initial' && this.state.source === 'collection-referrer') return;

        this.update({
          family: 'bandware',
          wearWith: normalizeWear(detail.wear),
          setMode: normalizeSetMode(detail.setMode, this.data.forceCompleteSet === true),
          appleWatchSize: detail.appleSize || '',
          royalPopModel: detail.royalModel || '',
          connectionFamily: detail.connectionFamily || (normalizeWear(detail.wear) === 'royal-pop' ? 'B¹ Frame' : 'B¹ Link'),
          linkSize: detail.linkSize || '',
          connectionFinish: detail.connectionFinish || '',
          customerConfirmed: incomingConfirmed || this.state.customerConfirmed === true,
        }, { source: effectiveSource });
      });

      document.addEventListener('product-recommendations:loaded', (event) => {
        if (!event.target?.matches?.('[data-bandware-recommendations-scope]')) return;
        this.syncBandwareCardsForCurrentState(event.target);
      });

      document.addEventListener('brilinton:product-gallery:dom-updated', () => this.renderBandwareUi(document));
      document.addEventListener('brilinton:pdp-section-refreshed', () => this.renderBandwareUi(document));
      document.addEventListener('shopify:section:load', (event) => this.refresh(event.target || document));
      document.addEventListener('shopify:section:unload', (event) => {
        if (!event.target?.querySelector?.(DATA_SELECTOR)) return;
        if (this.cardFrame) window.cancelAnimationFrame(this.cardFrame);
        this.cardFrame = 0;
        this.initialized = false;
      });
      window.addEventListener('popstate', () => this.syncFromUrl('popstate'));
    }

    init(scope = document) {
      const dataNode = scope.querySelector?.(DATA_SELECTOR) || document.querySelector(DATA_SELECTOR);
      const data = parseJson(dataNode, null);
      if (!data) return;

      const params = new URL(window.location.href).searchParams;
      const rememberedApple = window.BrilintonDeviceContext?.get('appleWatch');
      const rememberedRoyal = window.BrilintonDeviceContext?.get('royalPop');
      const explicitWear = normalizeWear(params.get('wear'));
      const referrerWear = explicitWear ? '' : wearFromCollectionReferrer();
      const resolvedWear = explicitWear || referrerWear;
      const family = normalize(data.family);
      const guidedBandware = data.guidedBandware === true || family === 'bandware';
      const defaultWear = guidedBandware ? (normalizeWear(data.defaultWear) || 'apple-watch') : '';

      if (this.cardFrame) {
        window.cancelAnimationFrame(this.cardFrame);
        this.cardFrame = 0;
      }
      this.data = data;
      this.state = {
        sectionId: String(data.sectionId || ''),
        productId: String(data.productId || ''),
        productHandle: data.productHandle || '',
        productTitle: data.productTitle || '',
        family,
        guidedBandware,
        wearWith: resolvedWear || defaultWear,
        appleWatchSize: params.get('size') || rememberedApple?.size || '',
        royalPopModel: params.get('model') || (rememberedRoyal?.modelSlug || rememberedRoyal?.handle) || '',
        setMode: normalizeSetMode(params.get('set'), data.forceCompleteSet === true),
        connectionFamily: resolvedWear === 'royal-pop' ? 'B¹ Frame' : (guidedBandware ? 'B¹ Link' : ''),
        linkSize: '',
        connectionFinish: '',
        customerConfirmed: Boolean(resolvedWear),
        source: explicitWear ? 'url' : (referrerWear ? 'collection-referrer' : 'theme-default'),
        contextCollectionPath: resolvedWear === 'royal-pop'
          ? (data.routes?.royalPopBandware || '/collections/royal-pop-bands')
          : (resolvedWear === 'apple-watch' ? (data.routes?.appleWatchBandware || '/collections/apple-watch-bands') : ''),
        galleryContext: resolvedWear === 'royal-pop'
          ? 'royal-pop'
          : (resolvedWear === 'apple-watch' ? (/49/.test(String(params.get('size') || '')) ? 'apple-watch-ultra' : 'apple-watch-standard') : ''),
      };
      this.initialized = true;
      this.revision += 1;

      this.renderBandwareUi(document);
      this.prepareBandwareCardsBeforeContextEvent(document);
      this.emit('initial');
      this.finishBandwareCardsAfterContextEvent(document);
    }

    syncFromUrl(source = 'url') {
      if (!this.initialized) return;
      const params = new URL(window.location.href).searchParams;
      const rememberedApple = window.BrilintonDeviceContext?.get('appleWatch');
      const rememberedRoyal = window.BrilintonDeviceContext?.get('royalPop');
      const explicitWear = normalizeWear(params.get('wear'));
      this.update({
        wearWith: explicitWear || this.state.wearWith,
        appleWatchSize: params.get('size') || rememberedApple?.size || '',
        royalPopModel: params.get('model') || (rememberedRoyal?.modelSlug || rememberedRoyal?.handle) || '',
        setMode: normalizeSetMode(params.get('set'), this.data.forceCompleteSet === true),
        customerConfirmed: Boolean(explicitWear),
      }, { source });
    }

    update(patch = {}, { source = 'context-update' } = {}) {
      if (!this.initialized) {
        this.init(document);
        if (!this.initialized) return;
      }

      const previous = clone(this.state);
      const next = { ...this.state, ...patch };
      next.wearWith = normalizeWear(next.wearWith) || this.state.wearWith || '';
      next.setMode = normalizeSetMode(next.setMode, this.data.forceCompleteSet === true);
      next.customerConfirmed = asBoolean(next.customerConfirmed, this.state.customerConfirmed);
      next.connectionFamily = next.wearWith === 'royal-pop' ? 'B¹ Frame' : (next.wearWith === 'apple-watch' ? 'B¹ Link' : '');
      next.contextCollectionPath = next.customerConfirmed && next.wearWith === 'royal-pop'
        ? (this.data.routes?.royalPopBandware || '/collections/royal-pop-bands')
        : (next.customerConfirmed && next.wearWith === 'apple-watch'
          ? (this.data.routes?.appleWatchBandware || '/collections/apple-watch-bands')
          : '');
      next.galleryContext = next.customerConfirmed && next.wearWith === 'royal-pop'
        ? 'royal-pop'
        : (next.customerConfirmed && next.wearWith === 'apple-watch'
          ? (/49/.test(String(next.appleWatchSize || '')) ? 'apple-watch-ultra' : 'apple-watch-standard')
          : '');
      next.source = source;
      this.state = next;
      this.revision += 1;

      this.renderBandwareUi(document);
      this.prepareBandwareCardsBeforeContextEvent(document);
      this.emit(source, previous);
      this.finishBandwareCardsAfterContextEvent(document);
    }

    emit(source, previous = null) {
      const context = this.get();
      document.dispatchEvent(new CustomEvent('brilinton:product-context-change', {
        detail: {
          context,
          previous,
          source,
          revision: this.revision,
        },
      }));
    }

    get() {
      return clone(this.state);
    }

    refresh(scope = document) {
      const dataNode = scope.querySelector?.(DATA_SELECTOR)
        || (scope.matches?.(DATA_SELECTOR) ? scope : null);
      const nextData = parseJson(dataNode, null);
      if (!this.initialized || (nextData && (
        String(nextData.sectionId || '') !== String(this.data.sectionId || '')
        || String(nextData.productId || '') !== String(this.data.productId || '')
      ))) {
        this.init(scope);
        return;
      }
      if (nextData) this.data = nextData;
      this.renderBandwareUi(scope);
      this.syncBandwareCardsForCurrentState(scope);
    }

    renderBandwareUi(scope = document) {
      if (!this.state.guidedBandware) return;
      const confirmed = this.state.customerConfirmed === true;
      const wear = this.state.wearWith;
      const routes = this.data.routes || {};

      queryWithin(scope, '[data-bandware-context-subtitle]').forEach((root) => {
        const link = root.querySelector('[data-bandware-context-subtitle-link]');
        const fallback = root.querySelector('[data-bandware-context-subtitle-fallback]');
        const contextual = confirmed && (wear === 'apple-watch' || wear === 'royal-pop');
        if (contextual && link) {
          link.textContent = wear === 'royal-pop' ? 'BandWare™ for Royal Pop' : 'BandWare™ for Apple Watch';
          link.setAttribute('href', wear === 'royal-pop'
            ? (routes.royalPopBandware || '/collections/royal-pop-bands')
            : (routes.appleWatchBandware || '/collections/apple-watch-bands'));
          link.hidden = false;
          if (fallback) fallback.hidden = true;
        } else {
          if (link) {
            link.hidden = true;
            link.removeAttribute('href');
          }
          if (fallback) {
            fallback.textContent = 'BandWare™';
            fallback.hidden = false;
          }
        }
        root.dataset.contextConfirmed = confirmed ? 'true' : 'false';
        root.dataset.wearWith = wear || '';
      });

      queryWithin(scope, '[data-bandware-context-clarity]').forEach((root) => {
        const configuration = root.querySelector('[data-bandware-context-configuration]');
        if (!configuration) return;

        let text = 'Choose what you’ll wear it with, and we’ll include the correct connection.';
        if (this.state.setMode === 'bandware-only') {
          text = 'BandWare only. A B¹ connection is not included in this configuration.';
        } else if (confirmed && wear === 'apple-watch') {
          text = this.state.appleWatchSize
            ? `Configured for Apple Watch ${this.state.appleWatchSize}. Matching B¹ Link included.`
            : 'Select your Apple Watch size, and we’ll include the matching B¹ Link.';
        } else if (confirmed && wear === 'royal-pop') {
          text = 'Configured for Royal Pop. Matching B¹ Frame included.';
        }
        configuration.textContent = text;
        root.dataset.contextConfirmed = confirmed ? 'true' : 'false';
        root.dataset.wearWith = wear || '';
      });
    }

    buildBandwareUrl(baseHref) {
      if (!baseHref) return '';
      let url;
      try { url = new URL(baseHref, window.location.origin); } catch (_) { return baseHref; }
      if (url.origin !== window.location.origin) return baseHref;

      if (!this.state.customerConfirmed || !this.state.wearWith) {
        url.searchParams.delete('wear');
        url.searchParams.delete('size');
        url.searchParams.delete('model');
        url.searchParams.delete('set');
        return `${url.pathname}${url.search}${url.hash}`;
      }

      url.searchParams.set('wear', this.state.wearWith);
      url.searchParams.set('set', this.state.setMode === 'bandware-only' ? 'bandware-only' : 'complete');
      if (this.state.wearWith === 'apple-watch') {
        if (this.state.appleWatchSize) url.searchParams.set('size', this.state.appleWatchSize);
        else url.searchParams.delete('size');
        url.searchParams.delete('model');
      } else {
        if (this.state.royalPopModel) url.searchParams.set('model', this.state.royalPopModel);
        else url.searchParams.delete('model');
        url.searchParams.delete('size');
      }
      return `${url.pathname}${url.search}${url.hash}`;
    }

    cardRecords(scope = document) {
      return queryWithin(scope, BANDWARE_CARD_SELECTOR).map((wrapper) => {
        const slider = wrapper.querySelector('.card-product__slider');
        if (!slider) return null;
        if (!wrapper.__brProductContextCard) {
          const productUrl = wrapper.dataset.bandwareProductUrl || slider.getAttribute('href') || '';
          const links = Array.from(wrapper.querySelectorAll('a[href]')).filter((link) => {
            try {
              return new URL(link.getAttribute('href'), window.location.origin).pathname === new URL(productUrl, window.location.origin).pathname;
            } catch (_) {
              return false;
            }
          });
          links.forEach((link) => {
            if (!link.dataset.bandwareContextBaseHref) link.dataset.bandwareContextBaseHref = link.getAttribute('href') || productUrl;
          });
          wrapper.__brProductContextCard = { wrapper, slider, links, productUrl };
        }
        return wrapper.__brProductContextCard;
      }).filter(Boolean);
    }

    updateBandwareCardLinks(scope = document) {
      this.cardRecords(scope).forEach((record) => {
        record.links.forEach((link) => {
          const base = link.dataset.bandwareContextBaseHref || record.productUrl;
          const next = this.buildBandwareUrl(base);
          if (next) link.setAttribute('href', next);
        });
      });
    }

    parseCardGallery(wrapper, selector) {
      const data = parseJson(wrapper.querySelector(selector), []);
      return Array.isArray(data)
        ? data.map((item) => typeof item === 'string' ? { src: item } : item).filter((item) => item?.src)
        : [];
    }

    originalCardImages(slider) {
      return Array.from(slider.children || []).filter((node) => node?.tagName === 'IMG' && !node.hasAttribute('data-product-context-card-image'));
    }

    notifyCardRefresh(record) {
      record.slider.dataset.currentSlide = '1';
      record.slider._requestedProductCardSlide = 0;
      if (record.slider.scrollLeft) record.slider.scrollLeft = 0;
      record.wrapper.dispatchEvent(new CustomEvent('brilinton:product-card-context-refresh', {
        bubbles: true,
        detail: { root: record.wrapper },
      }));
      const rail = record.wrapper.closest('.related-products-slider');
      window.requestAnimationFrame(() => {
        const flickity = rail && typeof window.Flickity?.data === 'function' ? window.Flickity.data(rail) : null;
        flickity?.resize?.();
      });
    }

    restoreCardRecord(record, { notify = true } = {}) {
      if (!record?.slider) return false;
      const hadContext = Boolean(record.slider.querySelector('[data-product-context-card-image]'));
      record.slider.querySelectorAll('[data-product-context-card-image]').forEach((node) => node.remove());
      this.originalCardImages(record.slider).forEach((image) => { image.hidden = false; });
      if (record.slider.dataset.bandwareCardContext === 'apple-watch') record.slider.removeAttribute('data-bandware-card-context');
      if (record.slider.dataset.cardMediaSource === 'product-context') record.slider.dataset.cardMediaSource = 'product';
      record.slider.removeAttribute('data-product-context-card-key');
      if (hadContext && notify) this.notifyCardRefresh(record);
      return hadContext;
    }

    restoreAppleCardMedia(scope = document) {
      this.cardRecords(scope).forEach((record) => this.restoreCardRecord(record));
    }

    createContextImage(item, index) {
      const image = document.createElement('img');
      const src = compactImageUrl(item.src, 750);
      image.src = src;
      image.srcset = [280, 530, 750, 950, 1200]
        .map((width) => `${compactImageUrl(item.src, width)} ${width}w`)
        .join(', ');
      image.sizes = '(min-width: 1400px) 315px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)';
      image.alt = item.alt || '';
      image.className = `motion-reduce${index === 0 ? ' active' : ''}`;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.setAttribute('data-product-context-card-image', '');
      if (Number(item.width) > 0) image.width = Number(item.width);
      if (Number(item.height) > 0) image.height = Number(item.height);
      return image;
    }

    applyAppleCardMedia(scope = document) {
      if (!this.state.customerConfirmed || this.state.wearWith !== 'apple-watch' || this.state.setMode !== 'complete') return;
      const ultra = this.state.galleryContext === 'apple-watch-ultra'
        || /49/.test(String(this.state.appleWatchSize || ''));

      this.cardRecords(scope).forEach((record) => {
        const selector = ultra
          ? '[data-bandware-card-apple-ultra-gallery]'
          : '[data-bandware-card-apple-standard-gallery]';
        const gallery = this.parseCardGallery(record.wrapper, selector);
        if (!gallery.length) {
          this.restoreCardRecord(record);
          return;
        }

        const contextKey = `${ultra ? 'apple-watch-ultra' : 'apple-watch-standard'}|${gallery.map((item) => item.src).join('|')}`;
        const existingContextImages = record.slider.querySelectorAll('[data-product-context-card-image]');
        if (record.slider.dataset.productContextCardKey === contextKey && existingContextImages.length === gallery.length) {
          this.originalCardImages(record.slider).forEach((image) => { image.hidden = true; });
          return;
        }

        record.slider.querySelectorAll('[data-product-context-card-image], .royal-pop-card-slide-overlay').forEach((node) => node.remove());
        this.originalCardImages(record.slider).forEach((image) => { image.hidden = true; });

        const fragment = document.createDocumentFragment();
        gallery.forEach((item, index) => fragment.appendChild(this.createContextImage(item, index)));
        record.slider.insertBefore(fragment, record.slider.firstChild);
        record.slider.dataset.bandwareCardContext = 'apple-watch';
        record.slider.dataset.cardMediaSource = 'product-context';
        record.slider.dataset.productContextCardKey = contextKey;
        record.slider.dataset.slides = String(gallery.length);
        this.notifyCardRefresh(record);
      });
    }

    prepareBandwareCardsBeforeContextEvent(scope = document) {
      this.updateBandwareCardLinks(scope);
      if (this.state.wearWith !== 'apple-watch' || !this.state.customerConfirmed || this.state.setMode !== 'complete') {
        this.restoreAppleCardMedia(scope);
      }
    }

    finishBandwareCardsAfterContextEvent(scope = document) {
      if (this.state.wearWith !== 'apple-watch' || !this.state.customerConfirmed || this.state.setMode !== 'complete') return;
      if (this.cardFrame) window.cancelAnimationFrame(this.cardFrame);
      this.cardFrame = window.requestAnimationFrame(() => {
        this.cardFrame = 0;
        this.updateBandwareCardLinks(scope);
        this.applyAppleCardMedia(scope);
      });
    }

    syncBandwareCardsForCurrentState(scope = document) {
      this.prepareBandwareCardsBeforeContextEvent(scope);
      this.finishBandwareCardsAfterContextEvent(scope);
    }
  }

  const service = new ProductContextService();
  window[GLOBAL_KEY] = service;
  window.BrilintonProductContext = {
    get: () => service.get(),
    update: (patch, options) => service.update(patch, options),
    refresh: (scope) => service.refresh(scope || document),
  };

  const boot = () => {
    if (!service.initialized) service.init(document);
    else service.refresh(document);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
